import { Controller } from '@hotwired/stimulus'
import {
  getElementFromEventTarget,
  isHashLink,
  scrollToHash
} from '../utils/anchor_scroll.js'

// Контроллер для плавного скролла к якорям на странице при клике на ссылки. Перехватывает клики по всем ссылкам и проверяет, являются ли они якорными ссылками текущей страницы. Если да, то выполняет плавный скролл к целевому элементу. Игнорирует ссылки, которые открываются в новой вкладке, имеют атрибут download или находятся внутри элемента с контроллером page-anchors (чтобы избежать конфликтов с локальной навигацией по странице).
export default class AnchorsController extends Controller {
  connect() {
    this.handleClick = this.handleClick.bind(this)
    document.addEventListener('click', this.handleClick)

    this.handleTurboLoad = this.handleTurboLoad.bind(this)
    document.addEventListener('turbo:load', this.handleTurboLoad)
  }

  disconnect() {
    document.removeEventListener('click', this.handleClick)
    document.removeEventListener('turbo:load', this.handleTurboLoad)
  }

  /**
   * При загрузке страницы с hash в URL корректирует позицию скролла с учётом
   * фиксированной шапки и панели якорей.
   * @returns {void}
   */
  handleTurboLoad() {
    const hash = window.location.hash
    if (!hash || hash === '#') return

    requestAnimationFrame(() => {
      scrollToHash(hash, { behavior: 'auto' })
    })
  }

  /**
   * Перехватывает клики по якорным ссылкам и выполняет плавный скролл по странице.
   * @param {MouseEvent} event - Событие клика по документу.
   * @returns {void}
   */
  handleClick(event) {
    if (event.defaultPrevented) return

    const target = getElementFromEventTarget(event.target)
    const anchor = target?.closest('a')
    if (!(anchor instanceof HTMLAnchorElement)) return

    if (!isHashLink(anchor)) return
    if (!this.shouldHandleAnchorClick(event, anchor)) return

    const href = anchor.getAttribute('href')
    if (!href || href === '#') return

    const isRootHashLink = href.startsWith('/#')
    if (isRootHashLink && window.location.pathname !== '/') return

    event.preventDefault()
    if (!scrollToHash(href, { behavior: 'smooth', updateHash: true })) {
      console.warn(`Цель для ${href} не найдена на странице.`)
    }
  }

  /**
   * Определяет, нужно ли контроллеру обрабатывать конкретный клик по ссылке.
   * @param {MouseEvent} event - Событие клика.
   * @param {HTMLAnchorElement} anchor - Ссылка, по которой кликнули.
   * @returns {boolean} `true`, если клик нужно обработать внутри контроллера.
   */
  shouldHandleAnchorClick(event, anchor) {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return false
    }

    if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
      return false
    }

    if (anchor.closest('[data-controller~="page-anchors"]')) {
      return false
    }

    return true
  }
}
